import React, { useEffect, useState } from 'react';

// ── <Avatar> ───────────────────────────────────────────────────────────────
// Pastille ronde façon Notion : initiale sur fond coloré, texte blanc.
// La couleur est stable par memberId (hash → palette), avec override pour les
// 3 membres fixes Café Sonore afin de garder un mapping visuel mémorable.

// Palette saturée originale — la couleur identifie le membre.
// Côté planning, on l'applique en pattern softTone : fond 12%, bordure 40%,
// texte plein. La couleur signale l'identité sans saturer la surface.
const PALETTE = [
  '#0000FF', // bleu pur
  '#8A2BE2', // bleu-violet
  '#FF45FF', // magenta
  '#FF4500', // orange-rouge
  '#51DA4C', // vert vif
  '#FFD400', // jaune
  '#00C4D9', // cyan
  '#FF2E92', // rose flashy
];

const COULEUR_FIXE = {
  TMJKzwnrx8zTrlwn: '#FF45FF',    // Charlotte
  TMoM9kAorev8g_vC: '#0000FF',    // Océane
  TMzzFNqDcu705BjY: '#8A2BE2',    // Extra
  'TMLkJEmrEdUXAZ-O': '#FF2E92', // Baptiste
};

export const MEMBRES_CONNUS = [
  { id: 'TMJKzwnrx8zTrlwn',  prenom: 'Charlotte', defaultColor: '#FF45FF' },
  { id: 'TMoM9kAorev8g_vC',  prenom: 'Océane',    defaultColor: '#0000FF' },
  { id: 'TMLkJEmrEdUXAZ-O',  prenom: 'Baptiste',  defaultColor: '#FF2E92' },
  { id: 'TMzzFNqDcu705BjY',  prenom: 'Extra',     defaultColor: '#8A2BE2' },
];

// Clé localStorage pour les overrides personnalisés.
export const LS_MEMBER_COLORS = 'sonore_member_colors';

export function getMemberColorOverrides() {
  try { return JSON.parse(localStorage.getItem(LS_MEMBER_COLORS) || '{}'); } catch { return {}; }
}

export function saveMemberColorOverrides(overrides) {
  try { localStorage.setItem(LS_MEMBER_COLORS, JSON.stringify(overrides)); } catch (_) {}
}

// Hash deterministe d'une chaîne en index de palette — garantit qu'un même
// memberId obtient toujours la même couleur (et que deux IDs voisins en diffèrent).
function hashIdx(str, mod) {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = ((h << 5) - h + str.charCodeAt(i)) | 0;
  return Math.abs(h) % mod;
}

export function couleurMembre(memberId, overrides) {
  if (!memberId) return PALETTE[0];
  const ov = overrides ?? getMemberColorOverrides();
  return ov[memberId] ?? COULEUR_FIXE[memberId] ?? PALETTE[hashIdx(memberId, PALETTE.length)];
}

// ── Couleur de texte lisible sur un fond donné (WCAG contrast)
// Renvoie '#000' ou '#FFF' selon ce qui contraste le mieux avec `bgHex`.
// Utile pour les blocs colorés où on veut du blanc sur les fonds saturés sombres
// et du noir sur les fonds clairs (jaune, magenta, vert, cyan).
function relLuminance(hex) {
  const c = hex.replace('#','');
  const v = (i) => {
    const x = parseInt(c.slice(i, i+2), 16) / 255;
    return x <= 0.03928 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * v(0) + 0.7152 * v(2) + 0.0722 * v(4);
}
export function texteSurFond(bgHex) {
  if (!bgHex || bgHex[0] !== '#') return '#FFFFFF';
  const L = relLuminance(bgHex);
  const cWhite = 1.05 / (L + 0.05);
  const cBlack = (L + 0.05) / 0.05;
  return cBlack >= cWhite ? '#000000' : '#FFFFFF';
}

// Cache global photoURL par memberId — alimenté par App au mount via /api/users.
// Mini emitter pour forcer le re-render des <Avatar> quand le cache est mis à jour
// (le fetch /api/users se résout après le premier paint, sinon les pastilles
// resteraient bloquées sur leurs initiales jusqu'à un refresh).
let PHOTO_CACHE = {};
const SUBSCRIBERS = new Set();
export function setMemberPhotos(map) {
  PHOTO_CACHE = { ...(map || {}) };
  SUBSCRIBERS.forEach(fn => { try { fn(); } catch (_) {} });
}
export function getMemberPhoto(memberId) {
  return memberId ? (PHOTO_CACHE[memberId] || null) : null;
}

export default function Avatar({ prenom, memberId, size = 24, style, overrideColor, photoUrl }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const cb = () => setTick(t => t + 1);
    SUBSCRIBERS.add(cb);
    return () => { SUBSCRIBERS.delete(cb); };
  }, []);

  const bg = overrideColor ?? couleurMembre(memberId);
  const fg = texteSurFond(bg);
  const initiale = (prenom || '?').trim().charAt(0).toUpperCase();
  const url = photoUrl ?? getMemberPhoto(memberId);
  if (url) {
    return (
      <div
        aria-label={prenom}
        title={prenom}
        style={{
          width: size, height: size, borderRadius: '50%',
          backgroundImage: `url(${url})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundColor: bg,
          flexShrink: 0, userSelect: 'none',
          ...style,
        }}
      />
    );
  }
  return (
    <div
      aria-label={prenom}
      title={prenom}
      style={{
        width: size, height: size, borderRadius: '50%',
        background: bg, color: fg,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'Inter', fontWeight: 600,
        fontSize: Math.round(size * 0.46),
        lineHeight: 1, letterSpacing: 0,
        flexShrink: 0, userSelect: 'none',
        ...style,
      }}
    >
      {initiale}
    </div>
  );
}
